import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { HardDrive, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';

function StorageManager() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('authToken');
  };

  useEffect(() => {
    fetchStorage();
  }, []);

  const fetchStorage = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/signage/storage`);
      setStorage(response.data);
    } catch (error) {
      console.error('Error fetching storage:', error);
      toast.error('Failed to load storage information');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('This will permanently delete all unused files from the server. Continue?')) {
      return;
    }

    setCleaning(true);
    try {
      const response = await axios.delete(`${API_URL}/api/signage/cleanup`);
      toast.success(`Cleanup complete! Deleted ${response.data.deletedFiles} files, freed ${response.data.freedSpace}`);
      fetchStorage();
    } catch (error) {
      console.error('Error cleaning up:', error);
      toast.error(error.response?.data?.message || 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  if (loading || !storage) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const percentageNum = parseFloat(storage.percentageUsed);
  const isWarning = percentageNum > 80;
  const isCritical = percentageNum > 90;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Storage Management</h2>
        <p className="text-sm text-gray-600">
          Monitor and manage your signage media storage
        </p>
      </div>

      {/* Storage Overview Card */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className={`p-4 rounded-lg ${
            isCritical ? 'bg-red-100' : isWarning ? 'bg-yellow-100' : 'bg-purple-100'
          }`}>
            <HardDrive className={`h-8 w-8 ${
              isCritical ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-purple-600'
            }`} />
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-gray-900">
              {storage.totalUsedFormatted}
            </h3>
            <p className="text-sm text-gray-600">
              of {storage.maxStorageFormatted} used
            </p>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${
              isCritical ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-purple-600'
            }`}>
              {storage.percentageUsed}
            </div>
            <p className="text-sm text-gray-600">Used</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                isCritical ? 'bg-red-600' : isWarning ? 'bg-yellow-500' : 'bg-purple-600'
              }`}
              style={{ width: storage.percentageUsed }}
            ></div>
          </div>
        </div>

        {/* Warning Messages */}
        {isCritical && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">Critical: Storage almost full!</p>
              <p className="text-sm text-red-700 mt-1">
                You're using over 90% of available storage. Delete unused files or contact support to increase storage capacity.
              </p>
            </div>
          </div>
        )}

        {isWarning && !isCritical && (
          <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-900">Warning: Storage running low</p>
              <p className="text-sm text-yellow-700 mt-1">
                You're using over 80% of available storage. Consider removing old or unused files.
              </p>
            </div>
          </div>
        )}

        {!isWarning && (
          <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-green-900">Storage healthy</p>
              <p className="text-sm text-green-700 mt-1">
                You have plenty of space available for new media files.
              </p>
            </div>
          </div>
        )}

        {/* Storage Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Total Used</p>
            <p className="text-2xl font-bold text-gray-900">{storage.totalUsedFormatted}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Available</p>
            <p className="text-2xl font-bold text-gray-900">{storage.availableFormatted}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Media Files</p>
            <p className="text-2xl font-bold text-gray-900">{storage.mediaCount}</p>
          </div>
        </div>
      </div>

      {/* Cleanup Section - Admin Only */}
      {user.role === 'admin' && (
        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Clean Up Unused Files</h3>
              <p className="text-sm text-gray-600 mb-4">
                Remove orphaned files that exist on the server but are not referenced in the database.
                This helps free up storage space and keeps your system clean.
              </p>
              <button
                onClick={handleCleanup}
                disabled={cleaning}
                className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                  cleaning
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                } text-white`}
              >
                <Trash2 className="h-5 w-5" />
                {cleaning ? 'Cleaning...' : 'Run Cleanup'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Storage Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-bold text-blue-900 mb-3">Tips for Managing Storage</h3>
        <ul className="space-y-2 text-sm text-blue-900">
          <li className="flex gap-2">
            <span>•</span>
            <span>Delete old or unused media files regularly from the Media Library tab</span>
          </li>
          <li className="flex gap-2">
            <span>•</span>
            <span>Compress videos before uploading - aim for 720p resolution at 2-5 Mbps bitrate</span>
          </li>
          <li className="flex gap-2">
            <span>•</span>
            <span>Optimize images before upload - PNG should be under 2MB, JPG under 1MB</span>
          </li>
          <li className="flex gap-2">
            <span>•</span>
            <span>Use MP4 format for videos with H.264 codec for best compatibility</span>
          </li>
          <li className="flex gap-2">
            <span>•</span>
            <span>Videos are automatically compressed on upload to save space</span>
          </li>
          <li className="flex gap-2">
            <span>•</span>
            <span>Remove announcements that are no longer relevant</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default StorageManager;